import { Amplify } from 'aws-amplify';
import { cognitoUserPoolsTokenProvider } from 'aws-amplify/auth/cognito';

// Auth cookie lifetime. Must stay in sync with the Cognito app client's
// refreshTokenValidity in infra/lib/stacks/serverless-stack.ts (30 days).
// Amplify rewrites the cookies on each token refresh, so the expiry slides:
// this bounds how long a dead refresh token lingers, it can't match it exactly.
export const AUTH_COOKIE_MAX_AGE_DAYS = 30;

// The landing (basiqr.com) shows "Go to app" instead of "Log in" when this
// cookie is on the shared .basiqr.com domain. It says someone is signed in
// on this browser and holds nothing else; landing/src/components/Nav.tsx
// reads it. The session cookies themselves stay on the app's host.
export const SIGNED_IN_MARKER_COOKIE = 'basiqr_signed_in';

const EXPIRED = 'expires=Thu, 01 Jan 1970 00:00:00 GMT';

// Configure AWS Amplify with Cognito settings; call once at startup
export const configureAmplify = () => {
  const userPoolId = import.meta.env.VITE_USER_POOL_ID;
  const userPoolWebClientId = import.meta.env.VITE_USER_POOL_CLIENT_ID;

  if (!userPoolId || !userPoolWebClientId) {
    const missingVars = [];
    if (!userPoolId) missingVars.push('VITE_USER_POOL_ID');
    if (!userPoolWebClientId) missingVars.push('VITE_USER_POOL_CLIENT_ID');

    throw new Error(
      `Missing required environment variables: ${missingVars.join(', ')}. ` +
      `Please check your .env file and ensure all required Cognito settings are configured.`
    );
  }

  Amplify.configure({
    Auth: {
      Cognito: {
        userPoolId,
        userPoolClientId: userPoolWebClientId,
        loginWith: {
          email: true,
        },
      },
    },
  });

  // Session cookies are host-only (pentest 2026-10-30 AUTH-6): no Domain
  // attribute, so only the app's own host can read them. They used to be set
  // on .basiqr.com, where script on any basiqr.com site could read the
  // 30-day refresh token.
  const hostname = window.location.hostname;
  const sharedDomain = hostname.endsWith('.basiqr.com') || hostname === 'basiqr.com' ? '.basiqr.com' : null;
  const secure = window.location.protocol === 'https:' ? ' secure;' : '';

  // Amplify's token store keys every cookie it writes under this prefix
  // (LastAuthUser, <username>.accessToken/idToken/refreshToken/clockDrift/...)
  const authCookiePrefix = `CognitoIdentityServiceProvider.${userPoolWebClientId}.`;

  const cookieValue = (name: string): string | null => {
    for (const part of document.cookie.split(';')) {
      const eq = part.indexOf('=');
      if (eq > -1 && part.slice(0, eq).trim() === name) return part.slice(eq + 1);
    }
    return null;
  };

  const expiresIn30Days = () =>
    new Date(Date.now() + AUTH_COOKIE_MAX_AGE_DAYS * 24 * 60 * 60 * 1000).toUTCString();

  const writeHostOnly = (name: string, value: string) => {
    document.cookie = `${name}=${value}; expires=${expiresIn30Days()}; path=/; SameSite=Lax;${secure}`;
  };

  // Every place an auth cookie may live: host-only (now), the shared domain
  // (until this change) and the bare hostname as a domain (older still).
  const expireCookie = (name: string) => {
    document.cookie = `${name}=; ${EXPIRED}; path=/`;
    if (sharedDomain) document.cookie = `${name}=; ${EXPIRED}; path=/; domain=${sharedDomain}`;
    document.cookie = `${name}=; ${EXPIRED}; path=/; domain=${hostname}`;
  };

  const setSignedInMarker = () => {
    if (!sharedDomain) return;
    document.cookie =
      `${SIGNED_IN_MARKER_COOKIE}=1; expires=${expiresIn30Days()}; path=/; domain=${sharedDomain}; SameSite=Lax;${secure}`;
  };
  const clearSignedInMarker = () => {
    if (sharedDomain) document.cookie = `${SIGNED_IN_MARKER_COOKIE}=; ${EXPIRED}; path=/; domain=${sharedDomain}`;
  };

  // A session signed in before this change sits on the shared domain: move it
  // to the app's host, keeping the user signed in, before Amplify reads it.
  if (sharedDomain) {
    const names = document.cookie
      .split(';')
      .map(part => part.split('=')[0].trim())
      .filter(name => name.startsWith(authCookiePrefix));
    for (const name of new Set(names)) {
      const value = cookieValue(name);
      document.cookie = `${name}=; ${EXPIRED}; path=/; domain=${sharedDomain}`;
      // Gone now means it only lived on the shared domain.
      if (value && cookieValue(name) === null) writeHostOnly(name, value);
    }
    if (names.some(name => name.endsWith('.idToken'))) setSignedInMarker();
  }

  // Configure token provider for cookies
  cognitoUserPoolsTokenProvider.setKeyValueStorage({
    setItem: async (key, value) => {
      writeHostOnly(key, value);
      if (key.startsWith(authCookiePrefix) && key.endsWith('.idToken')) setSignedInMarker();
      return;
    },
    getItem: async key => cookieValue(key) || null,
    removeItem: async key => {
      expireCookie(key);
      if (key.startsWith(authCookiePrefix) && key.endsWith('.idToken')) clearSignedInMarker();
      return;
    },
    // Only BasiQR auth cookies: theme, consent and analytics ids share the domain
    clear: async () => {
      const cookies = document.cookie.split(';');
      for (let i = 0; i < cookies.length; i++) {
        const cookie = cookies[i];
        const eqPos = cookie.indexOf('=');
        const name = eqPos > -1 ? cookie.substring(0, eqPos).trim() : cookie.trim();
        if (name.startsWith(authCookiePrefix)) {
          expireCookie(name);
        }
      }
      clearSignedInMarker();
      return;
    },
  });


};
